import clsx from 'clsx';
import { FC, ReactNode } from 'react';

import { RadioItem } from '@/components/shared/Fields/types';
import { Typography, TypographyVariant } from '@/components/shared/Typography';
import { isArrayNotEmpty } from '@/helpers/array';

import css from './style.module.scss';

interface RadioItemButtonProps {
	isActive: boolean;
	label: ReactNode;
	className?: string;
	onClick: () => void;
	typographyVariant?: TypographyVariant;
}

export const RadioItemButton: FC<RadioItemButtonProps> = ({
	isActive,
	label,
	onClick,
	className,
	typographyVariant = 'body-m'
}) => (
	<button
		type="button"
		className={clsx(css.radioButton, isActive ? css.active : '', className)}
		onClick={onClick}
	>
		<Typography tag="span" variant={typographyVariant} responsive={false}>
			{label}
		</Typography>
	</button>
);

interface Props {
	options: RadioItem[];
	selectedValue?: string | string[] | null;
	onChange?: (value: string | string[]) => void;
	multiple?: boolean;
	className?: string;
	typographyVariant?: TypographyVariant;
}

const Radio: FC<Props> = ({
	options,
	selectedValue,
	onChange,
	multiple = false,
	className,
	typographyVariant = 'body-m'
}) => {
	const isSelected = (value: string): boolean => {
		if (multiple && Array.isArray(selectedValue)) return selectedValue.includes(value);

		return value === selectedValue;
	};

	const handleClick = (value: string) => {
		if (!onChange) return;

		if (multiple) {
			const currentValues: string[] = Array.isArray(selectedValue) ? selectedValue : [];
			if (currentValues.includes(value)) onChange(currentValues.filter(v => v !== value));
			else onChange([...currentValues, value]);
		} else onChange(value);
	};

	return isArrayNotEmpty(options)
		? options.map(option => (
				<RadioItemButton
					key={option.id}
					className={className}
					isActive={isSelected(option.value)}
					label={option.label}
					typographyVariant={typographyVariant}
					onClick={() => handleClick(option.value)}
				/>
			))
		: null;
};

export default Radio;
